import React from 'react'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import FormControl from '@mui/material/FormControl'
import Radio from '@mui/material/Radio'
import TextField from '@mui/material/TextField'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'

import BottomButtonGroup from '../../component/BottomButtonGroup'
import { FORM_PANEL_SX, FORM_SUB_PANEL_SX, getFormChoiceCardSx } from '../../component/formSurfaceStyles'
import AppPushReserveFields from './AppPushReserveFields'

const SETTING_PANEL_SX = FORM_PANEL_SX

const getChoiceCardSx = (selected, disabled) => ({
    ...getFormChoiceCardSx(selected, disabled),
    px: 1.25,
    py: 1.25,
    minHeight: 78,
    width: '100%',
    '& .MuiRadio-root': {
        mt: -0.1,
        mr: 0.75,
        p: 0.25,
    },
    '& .MuiFormControlLabel-label': {
        width: '100%',
    },
})

const settingGroupPanelSx = {
    ...FORM_SUB_PANEL_SX,
}

const SETTING_GROUPS = [
    {
        title: '노출나라',
        name: 'cCode',
        defaultValue: 'kr',
        options: [
            { value: 'kr', title: '국내', description: '국내 발송' },
            { value: '99', title: '해외', description: '해외 발송' },
        ],
    },
    {
        title: '액션',
        name: 'pushType',
        defaultValue: 1,
        options: [
            { value: 1, title: '홈', description: '앱 홈으로 이동' },
            { value: 2, title: '애니멀런 대회', description: '선택 대회로 이동', disabledWhenOverseas: true },
            { value: 4, title: '우리끼리 대회', description: '선택 대회로 이동', disabledWhenOverseas: true },
        ],
    },
    {
        title: '발송',
        name: 'isReserve',
        defaultValue: 0,
        options: [
            { value: 0, title: '즉시발송', description: '저장 즉시 발송' },
            { value: 1, title: '예약발송', description: '예약 조건 발송' },
        ],
    },
    {
        title: '기종',
        name: 'deviceType',
        defaultValue: 0,
        options: [
            { value: 0, title: '전체', description: 'Android/iOS 전체 발송' },
            { value: 1, title: 'Android', description: 'Android만 발송' },
            { value: 2, title: 'iOS', description: 'iOS만 발송' },
        ],
    },
]

// 카드형 선택 UI에서도 기존 폼 변경 핸들러가 받던 name/value 이벤트 계약을 유지한다.
const createFieldChangeEvent = (name, value) => ({
    target: {
        name,
        value,
    },
})

function ChoiceCardLabel({ title, description }) {
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {description}
            </Typography>
        </Box>
    )
}

function SettingChoiceGroup({ group, params, saving, onFieldChange }) {
    const value = params[group.name] ?? group.defaultValue

    return (
        <Box>
            <FormControl fullWidth>
                <Box sx={settingGroupPanelSx}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        {group.title}
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: `repeat(${Math.min(group.options.length, 3)}, minmax(0, 1fr))` },
                            gap: 1,
                            mt: 1,
                        }}
                    >
                        {group.options.map((option) => {
                            const disabled = saving || (option.disabledWhenOverseas && params.cCode === '99')
                            const checked = String(value) === String(option.value)

                            return (
                                <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    control={(
                                        <Radio
                                            size="small"
                                            name={group.name}
                                            checked={checked}
                                            onChange={() => onFieldChange(createFieldChangeEvent(group.name, option.value))}
                                            disabled={disabled}
                                        />
                                    )}
                                    disabled={disabled}
                                    sx={getChoiceCardSx(checked, disabled)}
                                    label={(
                                        <ChoiceCardLabel
                                            title={option.title}
                                            description={option.description}
                                        />
                                    )}
                                />
                            )
                        })}
                    </Box>
                </Box>
            </FormControl>
        </Box>
    )
}

export default function AppPushFormFields({
    params,
    checked,
    saving,
    onSubmit,
    onFieldChange,
    onReserveHourChange,
    onReserveMinuteChange,
    onWeekTypeChange,
    onStartTimeChange,
    onEndTimeChange,
    listSearch,
    listCurPage,
    listSearchText,
    listSearchSelect,
    onBackClick,
    submitLabel,
}) {
    return (
        <Box component="form" encType="multipart/form-data" onSubmit={onSubmit}>
            <Grid container spacing={2} sx={{ marginTop: 2 }}>
                <Grid item xs={12}>
                    <Paper variant="outlined" sx={SETTING_PANEL_SX}>
                        <Box sx={{ mb: 1.5 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                푸시 설정
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                발송 대상, 이동 액션, 예약 발송 여부를 한 번에 확인합니다.
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                gap: 1.5,
                            }}
                        >
                            {SETTING_GROUPS.map((group) => (
                                <SettingChoiceGroup
                                    key={group.name}
                                    group={group}
                                    params={params}
                                    saving={saving}
                                    onFieldChange={onFieldChange}
                                />
                            ))}
                        </Box>
                    </Paper>
                </Grid>
                <AppPushReserveFields
                    isReserve={params.isReserve}
                    reserveHour={params.reserveHour}
                    reserveMinute={params.reserveMinute}
                    checked={checked}
                    saving={saving}
                    onReserveHourChange={onReserveHourChange}
                    onReserveMinuteChange={onReserveMinuteChange}
                    onWeekTypeChange={onWeekTypeChange}
                    startTime={params.startTime}
                    endTime={params.endTime}
                    onStartTimeChange={onStartTimeChange}
                    onEndTimeChange={onEndTimeChange}
                />
                <Grid item xs={12} sm={12} md={12}>
                    <FormControl fullWidth>
                        <TextField
                            label="푸시 내용"
                            required
                            id="content"
                            name="content"
                            multiline
                            rows={5}
                            value={params.content || ''}
                            size="small"
                            onChange={onFieldChange}
                            disabled={saving}
                        />
                    </FormControl>
                </Grid>
            </Grid>
            <BottomButtonGroup
                path="/appPushs"
                search={listSearch}
                visible={true}
                curPage={listCurPage}
                searchText={listSearchText}
                searchSelect={listSearchSelect}
                submitDisabled={saving}
                submitLabel={submitLabel}
                onBackClick={onBackClick}
            />
        </Box>
    )
}
